import { useState, useEffect } from 'react';
import styled from 'styled-components';
import CommonWrapper from '../../../../components/ui_components/AdminWrapper';
import colors from '../../../../styles/colors';
import { apiAdminGetQuizzes, apiAdminDeleteQuiz } from '../../../../api/quizApi';
import type { AdminQuiz } from '../../../../types/models/quiz';
import QuizCreateModal from './QuizCreateModal';

// ─── Styled ───────────────────────────────────────────────────────────────────

const PageInner = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 40px 24px 80px;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 28px;
  flex-wrap: wrap;
  gap: 12px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #fff;
  margin: 0;
`;

const BtnPrimary = styled.button`
  padding: 8px 20px;
  border-radius: 4px;
  border: none;
  background-color: gold;
  color: #000;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:hover { background-color: #e6c200; }
`;

const QuizList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const QuizCard = styled.div`
  background-color: ${colors.bgColorBlack2};
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 8px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const QuizTop = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const QuizId = styled.span`
  font-size: 11px;
  color: rgba(255,255,255,0.3);
  flex-shrink: 0;
  margin-top: 2px;
`;

const QuestionText = styled.p`
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  margin: 0;
  flex: 1;
  line-height: 1.4;
`;

const BadgesRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const Badge = styled.span<{ $color: string }>`
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  border: 1px solid ${({ $color }) => $color};
  color: ${({ $color }) => $color};
`;

const ChoicesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-left: 4px;
`;

const ChoiceItem = styled.div<{ $correct: boolean }>`
  font-size: 13px;
  color: ${({ $correct }) => ($correct ? '#5adf7a' : 'rgba(255,255,255,0.5)')};
  display: flex;
  align-items: center;
  gap: 6px;

  &::before {
    content: '${({ $correct }) => ($correct ? '✓' : '·')}';
    font-size: ${({ $correct }) => ($correct ? '12px' : '16px')};
    color: ${({ $correct }) => ($correct ? '#5adf7a' : 'rgba(255,255,255,0.2)')};
    flex-shrink: 0;
    width: 12px;
  }
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const BtnEdit = styled.button`
  padding: 5px 14px;
  border-radius: 4px;
  border: 1px solid rgba(255,215,0,0.35);
  background: transparent;
  color: gold;
  font-size: 13px;
  cursor: pointer;
  &:hover { background-color: rgba(255,215,0,0.08); }
`;

const BtnDelete = styled.button`
  padding: 5px 14px;
  border-radius: 4px;
  border: 1px solid rgba(248,113,113,0.3);
  background: transparent;
  color: #f87171;
  font-size: 13px;
  cursor: pointer;
  &:hover { background-color: rgba(248,113,113,0.08); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const StatusText = styled.p`
  text-align: center;
  color: rgba(255,255,255,0.35);
  margin-top: 48px;
  font-size: 14px;
`;

const DeleteError = styled.p`
  font-size: 12px;
  color: #f87171;
  margin: 4px 0 0;
  text-align: right;
`;

// ─── Component ────────────────────────────────────────────────────────────────

const QuizListPage: React.FC = () => {
  const [quizzes, setQuizzes] = useState<AdminQuiz[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingQuiz, setEditingQuiz] = useState<AdminQuiz | undefined>(undefined);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deleteErrors, setDeleteErrors] = useState<Record<number, string>>({});

  useEffect(() => {
    apiAdminGetQuizzes()
      .then(setQuizzes)
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []);

  const openCreate = () => { setEditingQuiz(undefined); setModalOpen(true); };
  const openEdit = (q: AdminQuiz) => { setEditingQuiz(q); setModalOpen(true); };

  const handleSaved = (saved: AdminQuiz) => {
    setQuizzes((prev) => {
      const idx = prev.findIndex((q) => q.id === saved.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = saved;
        return next;
      }
      return [saved, ...prev];
    });
    setModalOpen(false);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Eliminare questa domanda?')) return;
    setDeletingId(id);
    setDeleteErrors((prev) => { const n = { ...prev }; delete n[id]; return n; });
    try {
      await apiAdminDeleteQuiz(id);
      setQuizzes((prev) => prev.filter((q) => q.id !== id));
    } catch {
      setDeleteErrors((prev) => ({ ...prev, [id]: 'Eliminazione fallita.' }));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <CommonWrapper>
      <PageInner>
        <PageHeader>
          <PageTitle>Gestione Quiz</PageTitle>
          <BtnPrimary onClick={openCreate}>+ Nuova Domanda</BtnPrimary>
        </PageHeader>

        {isLoading && <StatusText>Caricamento...</StatusText>}
        {!isLoading && quizzes.length === 0 && (
          <StatusText>Nessuna domanda ancora. Creane una!</StatusText>
        )}

        <QuizList>
          {quizzes.map((q) => (
            <QuizCard key={q.id}>
              <QuizTop>
                <QuizId>#{q.id}</QuizId>
                <QuestionText>{q.question}</QuestionText>
              </QuizTop>

              <BadgesRow>
                <Badge $color={q.is_active ? '#5adf7a' : 'rgba(255,255,255,0.3)'}>
                  {q.is_active ? 'Attiva' : 'Disattiva'}
                </Badge>
                {q.is_priority && <Badge $color="gold">Priorità</Badge>}
                <Badge $color="rgba(255,255,255,0.25)">{q.choices.length} risposte</Badge>
              </BadgesRow>

              <ChoicesList>
                {q.choices.map((c) => (
                  <ChoiceItem key={c.id} $correct={c.is_correct}>{c.text}</ChoiceItem>
                ))}
              </ChoicesList>

              <ActionsRow>
                <BtnEdit onClick={() => openEdit(q)}>Modifica</BtnEdit>
                <BtnDelete
                  disabled={deletingId === q.id}
                  onClick={() => handleDelete(q.id)}
                >
                  {deletingId === q.id ? '...' : 'Elimina'}
                </BtnDelete>
              </ActionsRow>
              {deleteErrors[q.id] && <DeleteError>{deleteErrors[q.id]}</DeleteError>}
            </QuizCard>
          ))}
        </QuizList>
      </PageInner>

      {modalOpen && (
        <QuizCreateModal
          quiz={editingQuiz}
          onClose={() => setModalOpen(false)}
          onSaved={handleSaved}
        />
      )}
    </CommonWrapper>
  );
};

export default QuizListPage;
